import { useLocale } from "@/hooks/useLocale";
import { useTranslation } from "react-i18next";
import DOMPurify from "dompurify";
import { useState, useCallback } from "react";
import {
  Carousel,
  type CarouselApi,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";
import { useGetAnnouncementInfiniteQuery } from "@/services/api.announcements";
import { Skeleton } from "@/components/ui/skeleton";
import type { ISingleAnnouncement } from "@/types/announcement.interface";
import AnnouncementModal from "@/components/announcement/AnnouncementModal";
import { AnnouncementFallbackImg } from "@/assets/images";
import { DashboardCard } from "@/components/common/DashboardCard";
import { Megaphone } from "lucide-react";
import { Spinner } from "@/components/ui/v2/spinner";

const PRIMARY = "100,56,255";

interface IProps {
  platformSectionType?: string;
  limit?: number;
}

export const AnnouncementsWidget = ({
  platformSectionType = "ALL",
  limit = 5,
}: IProps) => {
  const { formatDate } = useLocale();
  const { t } = useTranslation("my-space");
  const [carouselApi, setCarouselApi] = useState<CarouselApi>();
  const [announcementId, setAnnouncementId] = useState<number>();

  const {
    data: announcement,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isLoading,
  } = useGetAnnouncementInfiniteQuery(
    { limit, platformSectionType },
    { skip: false },
  );

  // Flatten pages
  const allAnnouncements =
    announcement?.pages.flatMap((page) => page.data.announcementData) || [];

  // Handle carousel scroll - fetch next page when near the end
  const handleCarouselSelect = useCallback(() => {
    if (!carouselApi || isFetching || !hasNextPage) return;

    const currentIndex = carouselApi.selectedScrollSnap();
    const totalSlides = allAnnouncements.length;

    // Fetch next page when user is 2 slides away from the end
    if (currentIndex >= totalSlides - 2) {
      fetchNextPage();
    }
  }, [
    carouselApi,
    isFetching,
    hasNextPage,
    allAnnouncements.length,
    fetchNextPage,
  ]);

  // Set up carousel API and event listener
  const onCarouselInit = useCallback(
    (api: CarouselApi) => {
      setCarouselApi(api);
      api?.on("select", handleCarouselSelect);
    },
    [handleCarouselSelect],
  );

  const EmptyState = (
    <div
      className="h-full rounded-xl border flex flex-col items-center justify-center gap-3"
      style={{
        background: `linear-gradient(150deg, rgba(${PRIMARY},0.05) 0%, rgba(${PRIMARY},0.10) 100%)`,
        borderColor: `rgba(${PRIMARY},0.20)`,
      }}
    >
      <div
        className="rounded-full p-4"
        style={{ background: `rgba(${PRIMARY},0.08)` }}
      >
        <Megaphone className="w-7 h-7 text-primary opacity-50" />
      </div>
      <div className="text-center">
        <p className="text-sm font-semibold text-gray-500">
          {t("dashboard.no_announcements")}
        </p>
        <p className="text-xs text-gray-400 mt-0.5">
          {t("dashboard.check_back")}
        </p>
      </div>
    </div>
  );

  return (
    <>
      <DashboardCard
        title={t("dashboard.announcements")}
        icon="/icons/interface-icons/sidebar/icon-3d-announcements.png"
        action={{
          label: t("ui.view_all"),
          link: "/announcements",
        }}
      >
        {!allAnnouncements || allAnnouncements.length === 0 ? (
          EmptyState
        ) : isLoading ? (
          <Spinner />
        ) : (
          <div className="relative h-full bg-white rounded-sm overflow-hidden border border-border w-full min-w-0 min-h-72">
            {/* Carousel */}
            <Carousel
              className="relative w-full h-full min-w-0"
              setApi={onCarouselInit}
              opts={{
                loop: false,
              }}
            >
              <CarouselContent className="min-w-0 h-full">
                {allAnnouncements.map((item: ISingleAnnouncement) => (
                  <CarouselItem
                    key={item.id}
                    className="min-w-0 cursor-pointer h-full"
                    onClick={() => setAnnouncementId(item.id)}
                  >
                    <div className="relative overflow-hidden h-full min-w-0">
                      {/* Background Image */}
                      <div
                        style={{
                          backgroundImage: `url(${item?.coverImageUrl ?? AnnouncementFallbackImg})`,
                        }}
                        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
                      />

                      {/* Gradient Overlay */}
                      <div className="absolute inset-0 bg-linear-to-b from-black/10 via-black/20 to-black/80" />

                      {/* Content - Positioned at bottom */}
                      <div className="absolute bottom-0 start-0 end-0 p-4 min-w-0">
                        <div className="space-y-2 min-w-0">
                          <div className="flex items-start justify-between gap-2 min-w-0">
                            <h4 className="text-lg font-semibold text-white drop-shadow-lg line-clamp-1 flex-1 min-w-0 wrap-break-word">
                              {item.subject}
                            </h4>
                            <div className="bg-gray-100/20 px-2 py-1 rounded text-[0.65rem] font-medium text-white whitespace-nowrap shadow-xs shrink-0">
                              {formatDate(
                                new Date(item.createdAt),
                                "d MMMM yyyy",
                              )}
                            </div>
                          </div>

                          {/* Rich Text Message */}
                          <div className="overflow-hidden min-w-0">
                            <div
                              className="text-xs text-white/95 drop-shadow-md line-clamp-3 min-w-0
                          **:text-white!
                          **:bg-transparent!
                          **:max-w-full
                          **:wrap-break-word
                          **:[word-wrap:break-word]
                          **:min-w-0
                          [&_p]:text-xs [&_p]:text-white [&_p]:mb-1 [&_p]:leading-none [&_p]:wrap-break-word
                          [&_strong]:font-semibold [&_strong]:text-white
                          [&_em]:italic [&_em]:text-white
                          [&_u]:underline [&_u]:text-white
                          [&_ol]:list-decimal [&_ol]:ms-4 [&_ol]:text-white [&_ol]:max-w-full
                          [&_ul]:list-disc [&_ul]:ms-4 [&_ul]:text-white [&_ul]:max-w-full
                          [&_li]:text-xs [&_li]:text-white [&_li]:mb-0.5 [&_li]:wrap-break-word
                          [&_h1]:text-sm [&_h1]:font-bold [&_h1]:text-white [&_h1]:mb-1 [&_h1]:wrap-break-word
                          [&_h2]:text-sm [&_h2]:font-semibold [&_h2]:text-white [&_h2]:mb-1 [&_h2]:wrap-break-word
                          [&_h3]:text-xs [&_h3]:font-semibold [&_h3]:text-white [&_h3]:mb-1 [&_h3]:wrap-break-word
                          [&_a]:text-white [&_a]:underline [&_a]:cursor-pointer [&_a]:wrap-break-word
                          [&_blockquote]:border-s-2 [&_blockquote]:border-white/50 [&_blockquote]:ps-2 [&_blockquote]:italic [&_blockquote]:text-white [&_blockquote]:wrap-break-word
                          [&_div]:max-w-full [&_div]:wrap-break-word"
                              dangerouslySetInnerHTML={{
                                __html: DOMPurify.sanitize(item.message || ""),
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </CarouselItem>
                ))}

                {/* Loading next page */}
                {isFetching && hasNextPage && (
                  <CarouselItem>
                    <div className="h-[320px] flex items-center justify-center bg-muted">
                      <Skeleton className="w-full h-full" />
                    </div>
                  </CarouselItem>
                )}
              </CarouselContent>

              {/* Navigation Arrows */}
              {allAnnouncements.length > 1 && (
                <>
                  <CarouselPrevious className="absolute start-2 top-1/2 -translate-y-1/2 bg-white text-primary hover:text-primary hover:bg-white border-0 shadow-md h-8 w-8" />
                  <CarouselNext className="absolute end-2 top-1/2 -translate-y-1/2 bg-white text-primary hover:text-primary hover:bg-white border-0 shadow-md h-8 w-8" />
                </>
              )}
            </Carousel>
          </div>
        )}
      </DashboardCard>

      {announcementId && (
        <AnnouncementModal
          isFromCompany={false}
          announcementId={announcementId}
          onClose={() => setAnnouncementId(undefined)}
        />
      )}
    </>
  );
};
